import React, { useState, useEffect } from 'react';
import { 
  FaRobot, 
  FaUser, 
  FaSearch, 
  FaFilter, 
  FaSort, 
  FaComments, 
  FaTimes, 
  FaCheck,
  FaExclamationTriangle,
  FaPaperPlane,
  FaTag,
  FaBriefcase,
  FaGraduationCap
} from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import ChatGptService from '../../services/ChatGptService';
import './ChatServiceAdmin.css';

const ChatServiceAdmin = () => {
  const { currentUser } = useAuth();
  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [adminReply, setAdminReply] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  
  // Detect if a chat is about services
  const isServiceRelatedChat = (messages) => {
    if (!messages || messages.length === 0) return false;
    
    // Check the initial messages from the user
    const userMessages = messages.filter(msg => !msg.isAdmin).map(msg => msg.content || '');
    
    if (userMessages.length === 0) return false;
    
    // Check if any message contains service-related keywords
    return userMessages.some(msg => ChatGptService.isServiceRequest(msg));
  };
  
  // Get service type from chat
  const getServiceType = (messages) => {
    if (!messages || messages.length === 0) return null;
    
    const userMessages = messages.filter(msg => !msg.isAdmin).map(msg => msg.content || '');
    
    if (userMessages.length === 0) return null;
    
    // Check for tutoring
    if (userMessages.some(msg => {
      const lowerMsg = msg.toLowerCase();
      return lowerMsg.includes('tutor') || 
             lowerMsg.includes('lesson') || 
             lowerMsg.includes('teaching') ||
             lowerMsg.includes('one on one') || 
             lowerMsg.includes('one-on-one');
    })) {
      return 'tutoring';
    }
    
    // Check for project help
    if (userMessages.some(msg => {
      const lowerMsg = msg.toLowerCase();
      return lowerMsg.includes('project') || 
             lowerMsg.includes('assignment') || 
             lowerMsg.includes('homework') ||
             lowerMsg.includes('coursework');
    })) {
      return 'project';
    }
    
    // General service request
    return 'service';
  };
  
  // Load admin chats
  useEffect(() => {
    const loadAdminChats = async () => {
      setIsLoading(true);
      try {
        const adminChats = await ChatGptService.getAdminChats();
        console.log("Loaded admin chats:", adminChats);
        setChats(adminChats);
        setIsLoading(false);
      } catch (err) {
        console.error('Error loading admin chats:', err);
        setError('Failed to load chat requests. Please try again.');
        setIsLoading(false);
      }
    };
    
    loadAdminChats();
    
    // Set up interval to refresh chats every 30 seconds
    const intervalId = setInterval(loadAdminChats, 30000);
    
    // Clean up on unmount
    return () => clearInterval(intervalId);
  }, []);
  
  // Subscribe to messages for selected chat
  useEffect(() => {
    let unsubscribe = null;
    
    if (selectedChat) {
      // Set up subscription to chat messages
      const messagesQuery = ChatGptService.subscribeToAdminChatMessages(
        selectedChat.id,
        (messages) => {
          console.log("Got chat messages:", messages);
          setChatMessages(messages);
        }
      );
      
      unsubscribe = messagesQuery;
    }
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [selectedChat]);
  
  // Handle sending admin reply
  const handleSendReply = async () => {
    if (!adminReply.trim() || !selectedChat || !currentUser) return;
    
    setIsSubmitting(true);
    
    try {
      await ChatGptService.sendAdminChatMessage(
        selectedChat.id,
        currentUser.uid,
        adminReply,
        true // is admin message
      );
      
      // Clear input
      setAdminReply('');
      
      // Assign admin to chat if not already assigned
      if (!selectedChat.assignedAdmin) {
        await ChatGptService.assignAdminToChat(
          selectedChat.id,
          currentUser.uid,
          currentUser.displayName || currentUser.email
        );
        
        // Update local state
        setSelectedChat({
          ...selectedChat,
          assignedAdmin: currentUser.uid,
          adminName: currentUser.displayName || currentUser.email
        });
      }
      
    } catch (err) {
      console.error('Error sending reply:', err);
      alert('Failed to send your reply. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Close chat
  const handleCloseChat = async () => {
    if (!selectedChat || !currentUser) return;
    
    if (window.confirm('Are you sure you want to close this chat?')) {
      try {
        await ChatGptService.closeAdminChat(
          selectedChat.id,
          currentUser.uid,
          'Chat closed by admin'
        );
        
        // Update local state
        setChats(chats.filter(chat => chat.id !== selectedChat.id));
        setSelectedChat(null);
        setChatMessages([]);
      } catch (err) {
        console.error('Error closing chat:', err);
        alert('Failed to close the chat. Please try again.');
      }
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    return date.toLocaleString();
  };
  
  // Render service badge based on service type
  const renderServiceBadge = (chatId) => {
    if (!selectedChat || selectedChat.id !== chatId || chatMessages.length === 0) return null;
    
    const serviceType = getServiceType(chatMessages);
    if (!serviceType) return null;
    
    let badgeText = '';
    let icon = null;
    
    switch (serviceType) {
      case 'tutoring':
        badgeText = 'Tutoring';
        icon = <FaGraduationCap />;
        break;
      case 'project':
        badgeText = 'Project Help';
        icon = <FaBriefcase />;
        break;
      case 'service':
        badgeText = 'Service';
        icon = <FaTag />;
        break;
      default:
        return null;
    }
    
    return (
      <span className={`service-badge ${serviceType}`}>
        {icon} {badgeText}
      </span>
    );
  };
  
  // Filter and sort chats
  const filteredChats = chats
    .filter(chat => {
      // Apply search filter
      const searchMatch = 
        chat.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        chat.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (chat.lastMessage && chat.lastMessage.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Apply status filter
      const statusMatch = filter === 'all' || chat.status === filter;
      
      return searchMatch && statusMatch;
    })
    .sort((a, b) => {
      if (sortBy === 'recent') {
        return new Date(b.lastMessageTime || 0) - new Date(a.lastMessageTime || 0);
      } else if (sortBy === 'oldest') {
        return new Date(a.lastMessageTime || 0) - new Date(b.lastMessageTime || 0);
      } else if (sortBy === 'username') {
        return (a.userName || '').localeCompare(b.userName || '');
      }
      return 0;
    });
  
  if (isLoading) {
    return (
      <div className="chat-admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading chat requests...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="chat-admin-error">
        <FaExclamationTriangle />
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  return (
    <div className="chat-admin-container">
      <div className="chat-admin-header">
        <h2><FaComments /> User Chat Requests</h2>
        <div className="chat-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search chats..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <FaSearch />
          </div>
          
          <div className="filter-dropdown">
            <FaFilter />
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Chats</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
            </select>
        </div>
        
          <div className="sort-dropdown">
            <FaSort />
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="recent">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="username">User Name</option>
              </select>
          </div>
        </div>
      </div>
      
      <div className="chat-admin-content">
        <div className="chat-list">
          {filteredChats.length === 0 ? (
            <div className="no-chats">
              <FaExclamationTriangle />
              <p>No chat requests found.</p>
        </div>
          ) : (
            filteredChats.map(chat => (
              <div 
                key={chat.id} 
                className={`chat-item ${selectedChat?.id === chat.id ? 'selected' : ''} ${chat.unreadCount > 0 ? 'unread' : ''}`}
                onClick={() => setSelectedChat(chat)}
              >
                <div className="chat-icon">
                  <FaUser />
                </div>
                <div className="chat-info">
                  <div className="chat-name">
                    {chat.userName}
                    {selectedChat?.id === chat.id && renderServiceBadge(chat.id)}
              </div>
                  <div className="chat-preview">
                    {chat.lastMessage ? (
                      <>
                        {chat.lastMessage.length > 50 
                          ? chat.lastMessage.substring(0, 50) + '...' 
                          : chat.lastMessage
                        }
                      </>
                    ) : (
                      <span className="no-message">New conversation</span>
                    )}
              </div>
                  <div className="chat-meta">
                    <span className="chat-time">{formatTime(chat.lastMessageTime)}</span>
                    <span className={`chat-status ${chat.status}`}>
                      {chat.status.charAt(0).toUpperCase() + chat.status.slice(1)}
                    </span>
                    {chat.unreadCount > 0 && (
                      <span className="unread-badge">{chat.unreadCount}</span>
                    )}
        </div>
      </div>
              </div>
            ))
          )}
              </div>
              
        <div className="chat-detail">
          {selectedChat ? (
            <>
              <div className="chat-detail-header">
                <div>
                  <h3>{selectedChat.userName}</h3>
                  <span className="user-id">ID: {selectedChat.userId}</span>
                  {renderServiceBadge(selectedChat.id)}
                </div>
                <div className="chat-actions">
                  <button 
                    className="close-chat-btn"
                    onClick={handleCloseChat}
                  >
                    <FaTimes /> Close Chat
                  </button>
                </div>
              </div>
              
              <div className="chat-messages">
                {chatMessages.length === 0 ? (
                  <div className="no-messages">
                    <p>No messages yet.</p>
            </div>
                ) : (
                  chatMessages.map(message => (
                    <div 
                      key={message.id} 
                      className={`chat-message ${message.isAdmin ? 'admin' : 'user'} ${message.isSystemMessage ? 'system' : ''}`}
                    >
                      <div className="message-icon">
                        {message.isAdmin ? <FaUser /> : <FaRobot />}
                      </div>
                      <div className="message-content">
                        <div className="message-header">
                          <span className="message-author">
                            {message.isAdmin 
                              ? (message.isSystemMessage ? 'System' : 'Admin') 
                              : selectedChat.userName}
                          </span>
                          <span className="message-time">
                            {formatTime(message.timestamp)}
                          </span>
                        </div>
                        <div className="message-text">
                          {message.content}
            </div>
          </div>
        </div>
                  ))
                )}
          </div>
          
              <div className="reply-form">
                <textarea
                  placeholder="Type your reply..."
                  value={adminReply}
                  onChange={(e) => setAdminReply(e.target.value)}
                  disabled={isSubmitting}
                ></textarea>
            <button 
                  className="send-btn"
                  onClick={handleSendReply}
                  disabled={!adminReply.trim() || isSubmitting}
                >
                  <FaPaperPlane /> {isSubmitting ? 'Sending...' : 'Send'}
            </button>
          </div>
            </>
          ) : (
            <div className="no-chat-selected">
              <FaComments />
              <p>Select a chat to view the conversation</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatServiceAdmin; 